import {createApi} from '@reduxjs/toolkit/query/react';
import baseQuery from 'src/redux/api/baseQuery';
import { IguardarArchivoRequest, IguardarArchivoResponse, IobtenerArchivoRequest,DepartamentoResp, DepartamentoResponse, EditarMaestroRequest, GuardarMaestroRequest, HorariosResp, InactivarMaestroRequest, MaestroPorTipoYUnidadRequest, MaestrosResponse, MunicipioResp, MunicipioResponse, PaisResponse, PerfilResp, SedeResp } from './types/maestros';


/**
 * API para la gestión de maestros
 * @see https://redux-toolkit.js.org/rtk-query/overview
 * @see https://redux-toolkit.js.org/rtk-query/usage/customizing-queries
 *
 */
export const maestrosApi = createApi({
	reducerPath: 'maestrosApi',
	baseQuery,
	tagTypes: ['paises', 'departamentos', 'municipios', 'maestros', 'perfiles', 'horarios', 'sedes'],
	endpoints: (builder) => ({
		obtenerMunicipios: builder.query<MunicipioResp[], string>({
			query(departamentoId) {
				return {
					url: `comunes/departamentos/${departamentoId}/municipios`,
					method: 'GET',
					credentials: 'include',
				};
			},
			providesTags: ['municipios'],
		}),
		obtenerDepartamentos: builder.query<DepartamentoResp[], void>({
			query() {
				return {
					url: 'comunes/departamentos',
					method: 'GET',
					credentials: 'include',
				};
			},
			providesTags: ['departamentos'],
		}),
		obtenerPerfiles: builder.query<PerfilResp[], void>({
			query() {
				return {
					url: 'comunes/perfiles',
					method: 'GET',
					credentials: 'include',
				};
			},
			providesTags: ['perfiles'],
		}),
		obtenerHorariosDisponibilidad: builder.query<HorariosResp[], void>({
			query() {
				return {
					url: 'comunes/horarios-disponibilidad',
					method: 'GET',
					credentials: 'include',
				};
			},
			providesTags: ['horarios'],
		}),
		obtenerSedes: builder.query<SedeResp[], void>({
			query() {
				return {
					url: 'comunes/sedes',
					method: 'GET',
					credentials: 'include',
				};
			},
			providesTags: ['sedes'],
		}),
		obtenerArchivo: builder.query<Blob, IobtenerArchivoRequest>({
			query({ adjuntoId }) {
				return {
					url: `v1/adjuntos`,
					method: 'GET',
					params: {
						adjuntoId
					},
					credentials: 'include',
					responseHandler: (response) => response.blob(),
				};
			},
		}),
		guardarArchivo: builder.mutation<IguardarArchivoResponse, IguardarArchivoRequest>({
			query({ file, aspiranteId }) {
				const formData = new FormData();
				formData.append('archivo', file);
				formData.append('aspiranteId', aspiranteId);
				return {
					url: 'adjuntos/cargar',
					method: 'POST',
					credentials: 'include',
					body: formData,
				};
			},
		}),


		// Jupiter
		obtenerPaises: builder.query<PaisResponse[], void>({
			query() {
				return {
					url: 'v1/maestras/paises',
					method: 'GET',
					credentials: 'include',
				};
			},
			providesTags: ['paises'],
		}),
		
		obtenerDepartamentosPorPais: builder.query<DepartamentoResponse[], string>({
			query() {
				return {
					url: 'comunes/departamentos',
					method: 'GET',
					credentials: 'include',
				};
			},
			providesTags: ['departamentos'],
		}),
		
		obtenerMunicipiosPorDepartamento: builder.query<MunicipioResponse[], string>({
			query(departamentoId) {
				return {
					url: `v1/maestras/departamentos/${departamentoId}/municipios`,
					method: 'GET',
					credentials: 'include',
				};
			},
			providesTags: ['municipios'],
		}),
		obtenerMaestrosPorTipo: builder.query<MaestrosResponse [], MaestroPorTipoYUnidadRequest>({
			query({ tipoMaestra, unidadId}) {
				return {
					url: 'v1/maestra-tipos',
					method: 'GET',
					credentials: 'include',
					params: { unidadId, tipoMaestra},
				};
			}, 
			providesTags: ['maestros'],
		}),
		guardarMaestra: builder.mutation<void, GuardarMaestroRequest>({
			query(body) {
				return {
					url: 'v1/maestra-tipos',
					method: 'POST',
					credentials: 'include',
					body,
				};
			},
			invalidatesTags: ['maestros'],
		}),
		activarInactivarMaestra: builder.mutation<void, InactivarMaestroRequest>({
			query({tipoMaestraId, razon}) {
				return {
					url: `v1/maestra-tipos/${tipoMaestraId}/activo`,
					method: 'PATCH',
					params: { razon },
					credentials: 'include',
				};
			},
			invalidatesTags: ['maestros'],
		}),
		editarMaestra: builder.mutation<void, EditarMaestroRequest>({
			query({tipoMaestraId, ...body}) {
				return {
					url: `v1/maestra-tipos/${tipoMaestraId}`,
					method: 'PUT',
					credentials: 'include',
					body,
				};
			},
			invalidatesTags: ['maestros'],
		}),
	}),
});

export const {
	useObtenerPaisesQuery,
	useObtenerDepartamentosPorPaisQuery,
	useObtenerDepartamentosQuery,
	useObtenerMunicipiosQuery,
	useObtenerPerfilesQuery,
	useObtenerHorariosDisponibilidadQuery,
	useObtenerSedesQuery,
	useObtenerMunicipiosPorDepartamentoQuery,
	useLazyObtenerMaestrosPorTipoQuery,
	useObtenerMaestrosPorTipoQuery,
	useGuardarMaestraMutation,
	useEditarMaestraMutation,
	useActivarInactivarMaestraMutation
} = maestrosApi;
